import { useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import { Search, X, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

interface SenderFiltersProps {
  query: string
  sortBy: string
  blocked: boolean
  unblocked: boolean
  seen30d: boolean
  seen3mo: boolean
  seen6mo: boolean
  seen1yr: boolean
  seenEq1: boolean
  seenGt1: boolean
  seenGt10: boolean
  seenGt30: boolean
  accountId: number
}

export default function SenderFilters({
  query: initialQuery,
  sortBy: initialSortBy,
  blocked,
  unblocked,
  seen30d,
  seen3mo,
  seen6mo,
  seen1yr,
  seenEq1,
  seenGt1,
  seenGt10,
  seenGt30,
  accountId,
}: SenderFiltersProps) {
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [sortBy, setSortBy] = useState(initialSortBy)
  const [filters, setFilters] = useState({
    blocked,
    unblocked,
    seen30d,
    seen3mo,
    seen6mo,
    seen1yr,
    seenEq1,
    seenGt1,
    seenGt10,
    seenGt30,
  })
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Keyboard shortcut: / to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== initialQuery) {
        applyFilters({ query: searchQuery })
      }
    }, 300)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Update URL when sort changes
  useEffect(() => {
    if (sortBy !== initialSortBy) {
      applyFilters({ sort_by: sortBy })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy])

  const applyFilters = (overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = {
      query: searchQuery || undefined,
      sort_by: sortBy,
      ...overrides,
    }

    // Add filter params
    if (filters.blocked) params.blocked = 'true'
    if (filters.unblocked) params.unblocked = 'true'
    if (filters.seen30d) params.seen_30d = 'true'
    if (filters.seen3mo) params.seen_3mo = 'true'
    if (filters.seen6mo) params.seen_6mo = 'true'
    if (filters.seen1yr) params.seen_1yr = 'true'
    if (filters.seenEq1) params.seen_eq_1 = 'true'
    if (filters.seenGt1) params.seen_gt_1 = 'true'
    if (filters.seenGt10) params.seen_gt_10 = 'true'
    if (filters.seenGt30) params.seen_gt_30 = 'true'

    router.get(
      `/accounts/${accountId}/senders`,
      params,
      { preserveState: true, preserveScroll: false, replace: true }
    )
  }

  const handleFilterChange = (filterKey: keyof typeof filters, value: boolean) => {
    const newFilters = { ...filters, [filterKey]: value }
    setFilters(newFilters)
  }

  const handleApplyFilters = () => {
    applyFilters()
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setSortBy('recently_added')
    setFilters({
      blocked: false,
      unblocked: false,
      seen30d: false,
      seen3mo: false,
      seen6mo: false,
      seen1yr: false,
      seenEq1: false,
      seenGt1: false,
      seenGt10: false,
      seenGt30: false,
    })
    router.get(
      `/accounts/${accountId}/senders`,
      {},
      { preserveState: false, preserveScroll: false, replace: true }
    )
  }

  const hasActiveFilters =
    searchQuery ||
    sortBy !== 'recently_added' ||
    Object.values(filters).some((v) => v)

  const activeFilterCount = Object.values(filters).filter((v) => v).length

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      {/* Search Input */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search by email address or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10 bg-gray-100 dark:bg-gray-700 text-blue-950 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 border-gray-300"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className="sm:w-48">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recently_added">Recently added</SelectItem>
            <SelectItem value="recently_seen">Recently seen</SelectItem>
            <SelectItem value="most_frequent">Most frequent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filter Popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="secondary" className="relative">
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs bg-blue-600 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="end">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-3">Status</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-blocked"
                    checked={filters.blocked}
                    onCheckedChange={(checked) =>
                      handleFilterChange('blocked', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-blocked"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Blocked
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-unblocked"
                    checked={filters.unblocked}
                    onCheckedChange={(checked) =>
                      handleFilterChange('unblocked', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-unblocked"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Unblocked
                  </Label>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
              <h4 className="font-medium text-sm mb-3">Last Seen</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-30d"
                    checked={filters.seen30d}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seen30d', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-30d"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Past 30 days
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-3mo"
                    checked={filters.seen3mo}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seen3mo', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-3mo"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Past 3 months
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-6mo"
                    checked={filters.seen6mo}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seen6mo', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-6mo"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Past 6 months
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-1yr"
                    checked={filters.seen1yr}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seen1yr', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-1yr"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Past year
                  </Label>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
              <h4 className="font-medium text-sm mb-3">Frequency</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-eq1"
                    checked={filters.seenEq1}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seenEq1', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-eq1"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Sent 1 email
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-gt1"
                    checked={filters.seenGt1}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seenGt1', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-gt1"
                    className="text-sm font-normal cursor-pointer"
                  >
                    More than 1 email
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-gt10"
                    checked={filters.seenGt10}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seenGt10', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-gt10"
                    className="text-sm font-normal cursor-pointer"
                  >
                    More than 10 emails
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-gt30"
                    checked={filters.seenGt30}
                    onCheckedChange={(checked) =>
                      handleFilterChange('seenGt30', checked === true)
                    }
                  />
                  <Label
                    htmlFor="filter-gt30"
                    className="text-sm font-normal cursor-pointer"
                  >
                    More than 30 emails
                  </Label>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-300 dark:border-gray-600 pt-4 flex gap-2">
              <Button
                onClick={handleApplyFilters}
                size="sm"
                className="flex-1"
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="secondary"
          onClick={handleClearFilters}
          className="sm:w-auto"
        >
          <X className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  )
}
